import { useTranslation } from 'react-i18next';

import { useGetAttention, useGetEmployeeAttention } from '@/api/generated/attention.ts';
import { useWorkspace } from '@/api/workspaces.ts';

import type { NavKey } from './navigation.ts';

export interface RailMark {
    count: number;
    // Something's new since the section was last opened (plan/DECISIONS.md D118).
    isNew: boolean;
    // Overdue or failed.
    isAlert: boolean;
    // What it says, for the item's label and tooltip.
    description: string;
}

type RailMarks = Partial<Record<NavKey | 'user', RailMark>>;

interface MarkInput {
    count?: number;
    isNew?: boolean;
    isAlert?: boolean;
    countLabel?: string;
}

// What the API answers (plan task 61) put on the rail's items: a count of what waits there, whether something's new,
// and red for what's overdue or failed. Members and a client's employee each read their own.
const useRailMarks = (): RailMarks => {
    const { t } = useTranslation();
    const isEmployee = useWorkspace().role === 'employee';
    const attention = useGetAttention({ query: { enabled: !isEmployee } }).data;
    const employeeAttention = useGetEmployeeAttention({ query: { enabled: isEmployee } }).data;

    const mark = ({ count = 0, isNew = false, isAlert = false, countLabel }: MarkInput): RailMark | undefined => {
        if (count === 0 && !isNew && !isAlert) return undefined;
        const description = [
            count > 0 && countLabel,
            isNew && t('rail.new'),
            isAlert && count === 0 && t('rail.failingFeeds'),
        ]
            .filter(Boolean)
            .join(', ');
        return { count, isNew, isAlert, description };
    };

    if (isEmployee) {
        if (!employeeAttention) return {};
        return {
            home: mark({
                count: employeeAttention.due_todo_count,
                isNew: employeeAttention.has_new_home,
                isAlert: employeeAttention.has_overdue_todos,
                countLabel: t('rail.dueTodos', { count: employeeAttention.due_todo_count }),
            }),
            recaps: mark({
                count: employeeAttention.acknowledgement_count,
                isNew: employeeAttention.has_new_recaps,
                countLabel: t('rail.acknowledgements', { count: employeeAttention.acknowledgement_count }),
            }),
            user: mark({ isAlert: employeeAttention.has_own_failing_feeds }),
        };
    }

    if (!attention) return {};
    return {
        home: mark({
            count: attention.due_todo_count,
            isNew: attention.has_new_home,
            isAlert: attention.has_overdue_todos,
            countLabel: t('rail.dueTodos', { count: attention.due_todo_count }),
        }),
        activities: mark({ isNew: attention.has_new_activities }),
        clients: mark({ isNew: attention.has_new_clients }),
        recaps: mark({
            count: attention.draft_count,
            isNew: attention.has_new_recaps,
            isAlert: attention.has_failed_drafts,
            countLabel: t('rail.drafts', { count: attention.draft_count }),
        }),
        sources: mark({
            count: attention.unfiled_source_count,
            isNew: attention.has_new_sources,
            isAlert: attention.has_failed_sources,
            countLabel: t('rail.unfiledSources', { count: attention.unfiled_source_count }),
        }),
        connections: mark({ isAlert: attention.has_failing_feeds }),
        user: mark({ isAlert: attention.has_own_failing_feeds }),
    };
};

export default useRailMarks;
